import React, { useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginForm from './components/LoginForm';
import Navbar from './components/Navbar';
import PropertyList from './components/PropertyList';
import MapComponent from './components/MapComponent';
import Analytics from './components/Analytics';
import UserManagement from './components/UserManagement';
import { useProperties } from './hooks/useProperties';
import { Property } from './types/property.types';
import './index.css';

type TabType = 'list' | 'map' | 'analytics' | 'users';

const MainApp: React.FC = () => {
  const { user, canEdit } = useAuth();
  const { properties, loading, error } = useProperties();
  const [activeTab, setActiveTab] = useState<TabType>('list');
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);

  const tabs = [
    { id: 'list' as TabType, name: 'Lista de Propiedades', icon: '🏠', requiresAdmin: false },
    { id: 'map' as TabType, name: 'Mapa Interactivo', icon: '🗺️', requiresAdmin: false },
    { id: 'analytics' as TabType, name: 'Analytics', icon: '📊', requiresAdmin: false },
    { id: 'users' as TabType, name: 'Gestión de Usuarios', icon: '👥', requiresAdmin: true }
  ];

  // Filtrar tabs según permisos del usuario
  const availableTabs = tabs.filter(tab => !tab.requiresAdmin || canEdit);

  const handlePropertySelect = (property: Property) => {
    setSelectedProperty(property);
    if (activeTab !== 'map') {
      setActiveTab('map');
    }
  };

  if (!user) {
    return <LoginForm />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Tab Navigation */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {availableTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="text-lg">{tab.icon}</span>
                <span>{tab.name}</span>
                {tab.id === 'list' && properties.length > 0 && (
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                    {properties.length}
                  </span>
                )}
                {tab.requiresAdmin && (
                  <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded-full">
                    Admin
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error al cargar datos</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'list' && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Portafolio de Propiedades</h1>
                <p className="mt-2 text-lg text-gray-600">
                  Gestiona y explora nuestro catálogo completo de inmuebles
                </p>
              </div>
              <PropertyList 
                selectedProperty={selectedProperty}
                onPropertySelect={handlePropertySelect}
              />
            </div>
          )}

          {activeTab === 'map' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Mapa Interactivo</h1>
                  <p className="mt-2 text-lg text-gray-600">
                    Explora las propiedades por ubicación geográfica
                  </p>
                </div>
                {selectedProperty && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md">
                    <p className="text-sm font-medium text-blue-800">Propiedad Seleccionada:</p>
                    <p className="text-lg font-semibold text-blue-900">
                      {selectedProperty.tipo_inmueble} en {selectedProperty.sector}
                    </p>
                    <button
                      onClick={() => setActiveTab('list')}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      Ver en lista →
                    </button>
                  </div>
                )}
              </div>
              
              <div className="bg-white rounded-xl shadow-lg overflow-hidden" style={{ height: 'calc(100vh - 300px)' }}>
                <MapComponent
                  properties={properties}
                  selectedProperty={selectedProperty}
                  onPropertySelect={setSelectedProperty}
                />
              </div>
              
              {properties.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>💡 Tip: Haz clic en los marcadores para ver detalles de cada propiedad</span>
                    <span>{properties.length} propiedades mostradas</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Dashboard Analytics</h1>
                <p className="mt-2 text-lg text-gray-600">
                  Análisis completo del portafolio y métricas de rendimiento
                </p>
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando analytics...</p>
                  </div>
                </div>
              ) : properties.length > 0 ? (
                <Analytics properties={properties} />
              ) : (
                <div className="text-center py-12">
                  <div className="mx-auto h-24 w-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <svg className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No hay datos para analizar</h3>
                  <p className="text-gray-500">Los analytics se mostrarán cuando haya propiedades cargadas</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'users' && canEdit && (
            <UserManagement />
          )}
        </div>
      </main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
};

export default App;